import React, { useState } from "react";
import { Modal, Checkbox, Tag, Typography, message } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import approvalApi from "../../api/approvalApi";
import "./Approvals.css";

const { Text } = Typography;

/**
 * How each discarded level's outcome reads. Same four terminal stage statuses
 * `ApprovalTimeline`'s (unexported) `STATUS_DISPLAY` already names - kept as
 * its own small map here rather than reaching into that file.
 */
const OUTCOME_TAG = {
  APPROVED: { color: "green", label: "Approved" },
  REJECTED: { color: "red", label: "Rejected" },
  SKIPPED: { color: "default", label: "Skipped" },
  BYPASSED: { color: "purple", label: "Bypassed" },
};

const formatActedAt = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return null;
  }
};

/**
 * The body of the restart confirmation. Its own component (not inline JSX
 * built from closure variables) so its one interactive control - the
 * acknowledgment checkbox - can hold real React state; `onAcknowledgeChange`
 * is how that state reaches back out to the `Modal.confirm` handle sitting
 * outside React, so the primary button can be enabled/disabled from it.
 */
const RestartConfirmBody = ({ requestLabel, discardedLevels, hasDiscardableProgress, onAcknowledgeChange }) => {
  const [acknowledged, setAcknowledged] = useState(false);

  const handleChange = (event) => {
    setAcknowledged(event.target.checked);
    onAcknowledgeChange(event.target.checked);
  };

  return (
    <div className="aw-restart-confirm">
      <div className="aw-restart-confirm-banner">
        This cannot be undone. The current chain is closed and a brand-new one starts at Level 1,
        under today's configuration.
      </div>

      {hasDiscardableProgress ? (
        <>
          <p>
            Restarting <strong>{requestLabel}</strong> discards everything already decided on its
            current chain:
          </p>
          <ul className="aw-restart-discard-list">
            {discardedLevels.map((level) => {
              const outcome = OUTCOME_TAG[level.status] || OUTCOME_TAG.SKIPPED;
              const actedAt = formatActedAt(level.actedAt);
              return (
                <li key={level.stageNumber} className="aw-restart-discard-item">
                  <span className="aw-restart-discard-level">
                    Level {level.stageNumber} · {level.stageName}
                  </span>
                  <Tag color={outcome.color}>{outcome.label}</Tag>
                  {level.actedBy && <span className="aw-restart-discard-actor">by {level.actedBy}</span>}
                  {actedAt && <span className="aw-restart-discard-time">{actedAt}</span>}
                </li>
              );
            })}
          </ul>
        </>
      ) : (
        <p>
          Nothing has been decided on this chain yet; restarting mainly just picks up today's
          configuration.
        </p>
      )}

      <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>
        The old chain cannot be resumed once this is confirmed. The new request opens at Level 1 and
        is resolved against the workflow configured today, even where that differs from what this
        request was originally raised under.
      </Text>

      <Checkbox className="aw-restart-ack" checked={acknowledged} onChange={handleChange}>
        I understand this discards the recorded progress above
      </Checkbox>
    </div>
  );
};

/**
 * Preview, then confirm, then apply "discard progress, restart at Level 1"
 * for one request (D-EX1/D-EX6, Phase EX-5,
 * docs/approval-workflow/17-existing-data-implementation-plan.md).
 *
 * Shares `getRestartPreview`/`postRestart`'s busy/applied contract with
 * `offerChainBackfill`/`offerOverrideSync` (`onBusy`, `onApplied`) so a
 * caller wires it up the same way, but the confirmation itself is
 * deliberately built to not resemble either of those: this is the one action
 * in the whole feature that destroys already-recorded progress, and D-EX6
 * requires it never read as a third flavour of "apply settings to open
 * requests" - a filled red icon (not the plain `icon: null` those two use), a
 * named list of exactly what would be discarded (not a count), and a
 * checkbox that must be explicitly ticked before the primary action enables.
 *
 * @param {Object} options
 * @param {string} options.requestId
 * @param {string} [options.moduleLabel] - display name, for the dialog copy
 * @param {Function} [options.onBusy] - called with true/false around each call
 * @param {Function} [options.onApplied] - called with `postRestart`'s
 *   `data` (`{oldRequestId, newRequestId, moduleKey, discardedLevels}`) after
 *   a successful restart. Passed through (unlike the backfill/sync prompts,
 *   which call this with no arguments) because a restart's whole point is
 *   that the old id stops meaning anything - a caller that wants to show the
 *   fresh chain needs `newRequestId` to know what to fetch next.
 * @returns {Promise<void>} resolves once the Admin has answered
 */
export const offerChainRestart = async ({ requestId, moduleLabel, onBusy, onApplied }) => {
  onBusy?.(true);
  const preview = await approvalApi.getRestartPreview(requestId);
  onBusy?.(false);

  if (preview.status !== 200) {
    message.error(preview.message || "Could not check this request's progress");
    return;
  }

  const { discardedLevels = [], hasDiscardableProgress } = preview.data || {};
  const requestLabel = moduleLabel || "this request";

  // Wrapped in a Promise for the same reason as offerChainBackfill/
  // offerOverrideSync: Modal.confirm returns as soon as the dialog is shown,
  // not when it is answered.
  await new Promise((resolve) => {
    let acknowledged = false;
    let modalHandle;

    const handleAcknowledgeChange = (checked) => {
      acknowledged = checked;
      modalHandle.update({ okButtonProps: { danger: true, disabled: !acknowledged } });
    };

    modalHandle = Modal.confirm({
      className: "aw-restart-confirm-modal",
      icon: <ExclamationCircleFilled className="aw-restart-confirm-icon" />,
      title: "Admin: discard progress and restart at Level 1",
      width: 560,
      content: (
        <RestartConfirmBody
          requestLabel={requestLabel}
          discardedLevels={discardedLevels}
          hasDiscardableProgress={hasDiscardableProgress}
          onAcknowledgeChange={handleAcknowledgeChange}
        />
      ),
      okText: "Discard progress and restart",
      okButtonProps: { danger: true, disabled: true },
      cancelText: "Leave it as it is",
      // Returning a promise keeps the OK button loading until the write
      // finishes, so the dialog cannot be dismissed mid-restart.
      onOk: async () => {
        onBusy?.(true);
        const response = await approvalApi.postRestart(requestId, { confirmDiscardProgress: true });
        onBusy?.(false);

        if (response.status !== 200) {
          // Server message already reads correctly for every refusal reason
          // (NOT_PENDING / CONFIRMATION_REQUIRED / ALREADY_DECIDED /
          // RECREATE_FAILED / NOT_FOUND) - surfaced as-is, not re-derived.
          message.error(response.message || "Could not restart the approval chain");
        } else {
          message.success(response.message);
          onApplied?.(response.data);
        }
        resolve();
      },
      onCancel: () => resolve(),
    });
  });
};

export default offerChainRestart;
